import type { MouseEvent, ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from './cn';

/**
 * One action on a table row, drawn as an icon: 👁 View, ✏ Edit, 🗑 Delete,
 * ⬇ Download and so on.
 *
 * The label is both the tooltip and the accessible name, so the icon never
 * has to explain itself. A click never reaches the row: most rows open their
 * record on click, and pressing Edit must not also open View.
 *
 * Give `href` for a download or an external file; otherwise `onClick`.
 */
export interface IconActionProps {
  label: string;
  icon: LucideIcon;
  onClick?: () => void;
  href?: string;
  /** Opens `href` in a new tab. */
  newTab?: boolean;
  /** Red on hover: delete, remove, revoke, reject. */
  danger?: boolean;
  /** The row's main next step, drawn in the primary colour. */
  primary?: boolean;
  disabled?: boolean;
  loading?: boolean;
}

const BASE =
  'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-btn transition-colors ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ' +
  'disabled:cursor-not-allowed disabled:opacity-40 ' +
  // A finger needs more than a mouse pointer.
  '[@media(pointer:coarse)]:h-10 [@media(pointer:coarse)]:w-10';

export function IconAction({
  label, icon: Icon, onClick, href, newTab, danger, primary, disabled, loading,
}: IconActionProps) {
  const tone = primary
    ? 'text-primary-600 hover:bg-primary-50 hover:text-primary-700'
    : danger
      ? 'text-neutral-500 hover:bg-danger-100 hover:text-danger-600'
      : 'text-neutral-500 hover:bg-neutral-100 hover:text-primary-700';
  const stop = (e: MouseEvent) => e.stopPropagation();
  const glyph = loading
    ? <span aria-hidden className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
    : <Icon aria-hidden className="h-4 w-4" />;

  if (href && !disabled) {
    return (
      <a
        href={href}
        aria-label={label}
        title={label}
        onClick={stop}
        {...(newTab ? { target: '_blank', rel: 'noreferrer' } : {})}
        className={cn(BASE, tone)}
      >
        {glyph}
      </a>
    );
  }
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled || loading}
      onClick={(e) => { e.stopPropagation(); onClick?.(); }}
      className={cn(BASE, tone)}
    >
      {glyph}
    </button>
  );
}

/** The icons of one row, right-aligned, with room between them for a finger. */
export function RowActions({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={cn('flex items-center justify-end gap-0.5 [@media(pointer:coarse)]:gap-2', className)}
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </div>
  );
}
